// Host-app обёртка поверх @ozdao/martyrs/modules/analytics.
// Базовый track(name, params) приходит из martyrs-store — он шлёт ивенты на наш бэк.
// Здесь — только именованные хелперы под бизнес-ивенты Traffers'а (csp_*, ai_*, plan_click и т.п.).
// Имена ивентов те же что зарегистрированы в Метрике (см. scripts/monitor/audit.sh:30-48).
//
// purchase здесь нет: единственный источник истины — server-side хук
// payment.confirmed → purchase_subscription | purchase_pack в app/src/server.js
// (trackPaymentPurchase, exactly-once по providerPaymentId).

import { useAnalytics as useMartyrsAnalytics } from '@ozdao/martyrs/modules/analytics/client';

// amount.value в бэкенде в копейках — делим на 100.
function _toMajor(amountValue) {
  const v = Number(amountValue ?? 0);
  return v / 100;
}

function _planToItem(plan) {
  if (!plan) return null;
  const price = _toMajor(plan?.amount?.value ?? plan?.price ?? 0);
  return {
    item_id: plan.slug || plan._id || 'unknown',
    item_name: plan.name || plan.slug || 'unknown',
    price,
    currency: plan?.amount?.currency || 'RUB',
  };
}

export function useAnalytics() {
  const m = useMartyrsAnalytics();
  // server-MP-адаптер подставляет dl из params.pageUrl (иначе — хардкод-дефолт,
  // и все события в Метрике приписываются одному URL). Прикладываем реальный
  // адрес страницы ко всем клиентским ивентам: origin+pathname без query,
  // чтобы yclid/utm/payment=success не зашумляли URL-отчёты. SSR-safe.
  const track = (name, params = {}) => {
    const p = { ...(params || {}) };
    if (!p.pageUrl && typeof window !== 'undefined' && window.location) {
      p.pageUrl = window.location.origin + window.location.pathname;
    }
    return m.track(name, p);
  };

  return {
    // generic
    track,
    // снимок trackers со всех адаптеров (для атрибуции платежа в момент оплаты)
    collectTrackers: m.collectTrackers,
    // route.meta.title в роутере объявлен как `{ ru: 'Главная' }` для будущей
    // i18n-локализации. Передавать в track как объект нельзя — server-MP-адаптер
    // делает String(obj) → '[object Object]'. Коэрсим к строке: предпочитаем
    // document.title (его выставляет useHead/$t), иначе берём ru-вариант или
    // первое строковое значение из объекта.
    pageView: (rawPath, rawTitle) => {
      let title = '';
      if (typeof rawTitle === 'string') {
        title = rawTitle;
      } else if (rawTitle && typeof rawTitle === 'object') {
        title = rawTitle.ru ?? Object.values(rawTitle).find(v => typeof v === 'string') ?? '';
      }
      if (!title && typeof document !== 'undefined' && document.title) {
        title = document.title;
      }
      return track('page_view', { path: rawPath, title });
    },

    // Auth — sign_up шлём с клиента: в момент успешного auth юзер сидит в браузере,
    // Метрика-тэг уже выставил _ym_uid, analytics-store сам приложит trackers
    // через collectTrackers() и server-adapter Метрики получит clientId/yclid
    // синхронно (без race с асинхронным client→server page_view track-ом).
    signUp: (method) => track('sign_up', { method }),
    login: (method) => track('login', { method }),

    // Searches
    createSearch: (params = {}) => track('create_search', params),

    // Create-Search popup funnel
    cspOpen: (source) => track('csp_open', { source: source || 'unknown' }),
    cspStepView: (step) => track('csp_step_view', { step }),
    cspStepNext: (fromStep, toStep, hasResume) => track('csp_step_next', { from_step: fromStep, to_step: toStep, has_resume: !!hasResume }),
    cspStepBack: (fromStep, toStep) => track('csp_step_back', { from_step: fromStep, to_step: toStep }),
    cspClose: (step, completed) => track('csp_close', { last_step: step, completed: !!completed }),
    cspSubmitAttempt: (params = {}) => track('csp_submit_attempt', params),
    cspSubmitError: (reason, code) => track('csp_submit_error', { reason: reason || 'unknown', code: code || null }),
    cspModeSwitch: (mode) => track('csp_mode_switch', { mode }),

    // AI buttons
    aiKeywordsClick: (q) => track('ai_keywords_click', { quota_remaining: q ?? null }),
    aiKeywordsSuccess: (q) => track('ai_keywords_success', { quota_remaining: q ?? null }),
    aiKeywordsError: (code, message) => track('ai_keywords_error', { code: code || 'GENERIC', message: message || null }),
    aiCoverClick: (q) => track('ai_cover_click', { quota_remaining: q ?? null }),
    aiCoverSuccess: (q) => track('ai_cover_success', { quota_remaining: q ?? null }),
    aiCoverError: (code, message) => track('ai_cover_error', { code: code || 'GENERIC', message: message || null }),

    // Onboarding & Resume gap
    onboardingAutoOpen: () => track('onboarding_auto_open', {}),
    noResumesShown: () => track('no_resumes_shown', {}),
    noResumesAction: (action) => track('no_resumes_action', { action }),

    // Онбординг-мастер /get-started
    onboardingStart: () => track('onboarding_start', {}),
    onboardingStep: (stepId, index) => track('onboarding_step_view', { step: stepId || null, index: index ?? null }),
    onboardingTimeline: (value) => track('onboarding_timeline', { timeline: value ?? null }),
    onboardingComplete: (params = {}) => track('onboarding_complete', params),

    // Публичные квиз-воронки с лендинга (web2app: интро → квиз → план → пейвол → оплата → вход)
    // Показ интро-экрана перед первым вопросом — меряем отвал ДО старта квиза
    // (в среднем ~50% уходят, не начав отвечать).
    introView: (quiz) => track('intro_view', { quiz: quiz || null }),
    quizStart: (quiz) => track('quiz_start', { quiz: quiz || null }),
    // value — выбранный ответ на шаге (для распределения ответов). Массивы (multi)
    // сериализуем в строку: server-MP-адаптер делает String(obj) → '[object Object]'.
    quizStep: (quiz, stepId, index, value) => track('quiz_step_view', {
      quiz: quiz || null, step: stepId || null, index: index ?? null,
      value: Array.isArray(value) ? value.join(',') : (value ?? null),
    }),
    quizComplete: (quiz, params = {}) => track('quiz_complete', { quiz: quiz || null, ...params }),
    // Показ email-гейта (сегодня синхронен quiz_complete — отдельная цель в Метрике не нужна).
    quizEmailView: (quiz) => track('quiz_email_view', { quiz: quiz || null }),
    leadCapture: (quiz) => track('lead_capture', { quiz: quiz || null }),
    // Показ экрана плана/результата после building-лоадера — закрывает слепую
    // зону email → building → продажа (падения генерации, уходы на анимации).
    quizResultView: (quiz) => track('quiz_result_view', { quiz: quiz || null }),
    // «Дошёл до шага оплаты» — платная микроцель Директа (PAY_FOR_CONVERSION).
    // Дедуп на вызывающей стороне: РОВНО один раз за прохождение квиза —
    // каждый reach цели в Директе стоит денег, paywall_view для этого не годится.
    quizPayStep: (quiz, params = {}) => track('quiz_pay_step', { quiz: quiz || null, ...params }),
    paywallView: (context) => track('paywall_view', { context: context || null }),

    // Резюме-воронка: ветка improve/generate, генерация с нуля, разблокировка
    // за пакет, fake-door «Загрузить на HeadHunter» (фичи ещё нет — меряем спрос).
    quizBranch: (quiz, branch) => track('quiz_branch', { quiz: quiz || null, branch: branch || null }),
    resumeGenerate: (status) => track('resume_generate', { status: status || null }),
    resumeUnlock: (mode, source) => track('resume_unlock', { mode: mode || null, source: source || null }),
    fakeDoorClick: (id, location) => track('fake_door_click', { feature: id || null, location: location || null }),

    // Резюме-онбординг /dashboard/resume-start
    resumeOnboardingStart: () => track('resume_onboarding_start', {}),
    resumeOnboardingStep: (stepId, index) => track('resume_onboarding_step_view', { step: stepId || null, index: index ?? null }),
    resumeOnboardingComplete: (mode) => track('resume_onboarding_complete', { mode: mode || null }),

    // Pricing & CTA
    viewPricing: (location) => track('view_pricing', { location: location || 'landing' }),
    clickCTA: (id, location, extra = {}) => track('cta_click', { cta_id: id, location: location || null, ...extra }),

    selectPlan: (plan, location) => {
      const item = _planToItem(plan);
      if (!item) return;
      track('plan_click', {
        plan_slug: item.item_id,
        plan_name: item.item_name,
        price: item.price,
        location: location || 'landing',
      });
    },

    beginCheckout: (plan) => {
      const item = _planToItem(plan);
      if (!item) return;
      track('begin_checkout', {
        currency: item.currency,
        value: item.price,
        plan_slug: item.item_id,
      });
    },

    // Старт триала без карты (клик по «Начать бесплатно — без карты»). Списания
    // после триала нет — платёж случается, только если юзер сам оформит подписку;
    // он трекается server-side как purchase_subscription (см. server.js
    // trackPaymentPurchase). Здесь — только факт старта пробного.
    trialStart: (plan, context) => {
      const item = _planToItem(plan);
      track('trial_start', {
        context: context || null,
        plan_slug: item?.item_id || null,
        value: item?.price ?? null,
      });
    },

    // Сторонние клики
    signupClick: (location) => track('signup_click', { location: location || null }),
    signinClick: (location) => track('signin_click', { location: location || null }),
    supportClick: (channel, location) => track('support_click', { channel: channel || null, location: location || null }),
    linkClick: (url, label, location) => track('link_click', { url, label: label || null, location: location || null }),
  };
}
